import type { AuthResponse, User } from '@/types/auth';
import { getFirebaseAuth } from '@/lib/firebase';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080';

/**
 * Session flag: set after a successful sign-in, cleared on logout.
 * Lets us skip loading the Firebase SDK entirely for anonymous visitors —
 * the flag is the only thing checked on page load.
 */
const SESSION_KEY = 'goreal:session';

export function hasSession(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

export function setSession(): void {
  try {
    window.localStorage.setItem(SESSION_KEY, '1');
  } catch {
    // storage unavailable (private mode) — auth still works for the tab
  }
}

export function clearSession(): void {
  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

/**
 * Authenticate with the backend using a Firebase ID token.
 * The backend verifies the token, creates/finds the user, and returns a session.
 */
export async function firebaseAuthApi(firebaseToken: string): Promise<AuthResponse> {
  const res = await fetch(`${API_URL}/api/v1/auth/firebase`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ firebase_token: firebaseToken }),
  });

  if (!res.ok) {
    const error = await res.text();
    throw new Error(error || 'Firebase authentication failed');
  }

  return res.json();
}

/**
 * Get current user profile using auth token
 */
export async function getProfile(token: string): Promise<User> {
  const res = await fetch(`${API_URL}/api/v1/me`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const error = await res.text();
    throw new Error(error || 'Failed to get profile');
  }

  return res.json();
}

/**
 * Update current user profile
 */
export async function updateProfileApi(
  token: string,
  data: Partial<
    Pick<User, 'first_name' | 'last_name' | 'phone' | 'is_first_time_buyer' | 'email_notifications'>
  >
): Promise<User> {
  const res = await fetch(`${API_URL}/api/v1/me`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    throw new Error('Failed to update profile');
  }

  return res.json();
}

/**
 * Get the current Firebase ID token for authenticated API requests.
 * Returns null when signed out — and never loads the Firebase SDK unless a
 * session flag says the visitor has signed in before.
 */
export async function getAuthToken(): Promise<string | null> {
  if (typeof window === 'undefined' || !hasSession()) {
    return null;
  }
  try {
    const auth = await getFirebaseAuth();
    await auth.authStateReady();
    const user = auth.currentUser;
    if (!user) return null;
    return await user.getIdToken();
  } catch {
    return null;
  }
}
